import { PencilIcon } from "@heroicons/react/solid";
import { format } from "date-fns";
import Cookies from "js-cookie";
import _get from "lodash/get";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import FormFieldsCard from "components/lib/Global/FormFieldsCard";
import Tabs from "components/lib/Shared/Tabs";
import TemplateSummary from "components/OrgAndSuperAdminDashboard/TemplatesSettings/Templates/SummaryForm";
import { Constants } from "config/constants";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import { updateTemplateState } from "state/slices/template";
import { setCookie } from "utils/cookie";
import { filterCompanyVersions } from "../History/filterCompanyVersions";
import RegistrarEditForm from "./CompanyDocumentsForm";

export default function CompanySummary({ dateFrom, dateTo, isHistory }) {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedField, setSelectedField] = useState(null);

  const dispatch = useDispatch();
  const { company, dashboardPermissions, dashboardRoles, companyOfficials } =
    useSelector((state) => state.companyDashboardSlice);

  const { latestVersion, previousVersion } = filterCompanyVersions({
    dateFrom,
    dateTo,
    company: company,
    companyOfficials: companyOfficials.items,
  });
  const formatDate = (date) => {
    if (!date?.getTime()) {
      // Return company's date of registration
      return format(new Date(company.registrationDate), "yyyy/MM/dd");
    }
    return format(date, "yyyy/MM/dd");
  };

  const fromDate = isHistory ? formatDate(dateFrom) : "";
  const toDate = isHistory ? formatDate(dateTo) : "";

  useEffect(() => {
    if (
      dashboardRoles?.some(
        (role) =>
          role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
      )
    ) {
      setCookie("role", Constants.COOKIE_ROLES.CONTACT_PERSON);
    }
  }, [dashboardRoles]);

  useEffect(() => {
    if (!company?._id) return;
    const { latestVersion: lv } = filterCompanyVersions({
      dateFrom,
      dateTo,
      company: company,
      companyOfficials: companyOfficials.items,
    });
    const selectedTemplate = isHistory
      ? lv?.companySummary?.articlesOfAssociation
      : company?.articlesOfAssociation?.template;
    dispatch(
      updateTemplateState({
        selectedTemplate,
      })
    );
  }, [
    company?._id,
    company?.articlesOfAssociation?.template,
    companyOfficials.items,
    dateFrom,
    dateTo,
    isHistory,
  ]);

  const handleFormOpen = (field) => {
    setSelectedField(field);
  };

  const handleFormClose = () => {
    setSelectedField(null);
  };

  const viewRestrictedData = dashboardPermissions.some(
    (permission) =>
      permission.name ===
      Roles_Permissions_Constants.DASHBOARD_PERMISSIONS.VIEW_RESTRICTED_DATA
  );

  const tabs = useMemo(() => {
    const commonTabs = [
      {
        id: "company",
        label: "1. Company Summary",
      },
    ];
    if (viewRestrictedData) {
      commonTabs.push({
        id: "governance",
        label: "2. Governance Summary",
      });
    }
    return commonTabs;
  }, [viewRestrictedData]);

  if (!company?._id) return "loading...";

  const { articlesOfAssociation: latestArticlesOfAssociation } =
    latestVersion?.companySummary ?? {};
  const { articlesOfAssociation: previousArticlesOfAssociation } =
    previousVersion?.companySummary ?? {};

  const historyVersions = isHistory
    ? (latestArticlesOfAssociation?.template?.versions ?? []).filter(
        (version) =>
          version.status === "Current" &&
          formatDate(
            new Date(version?.effectiveFrom || company.registrationDate)
          ) >= version?.effectiveFrom
            ? fromDate
            : toDate &&
              formatDate(
                new Date(version?.effectiveFrom || company.registrationDate)
              ) <= toDate
      )
    : [];

  const { contactPerson, articlesOfAssociation } = company;
  const templateVersions = articlesOfAssociation?.template?.versions ?? [];

  // Filter out draft versions and sort by versionNumber in descending order
  const nonDraftVersions = templateVersions
    .filter((version) => version.status !== "Draft")
    .sort((a, b) => {
      // Convert version numbers to numbers for proper sorting
      const versionA = parseFloat(a.versionNumber);
      const versionB = parseFloat(b.versionNumber);
      return versionB - versionA; // Descending order (latest first)
    });

  const latestTemplateVersion = isHistory
    ? historyVersions[0]
    : nonDraftVersions[0];

  //Handle history pages
  function getChangedFields(latestVersion, previousVersion) {
    const changedFields = [];

    // Compare companySummary fields
    const latestSummary = latestVersion.companySummary;
    const previousSummary = previousVersion.companySummary;

    for (const key in latestSummary) {
      if (latestSummary[key] !== previousSummary[key]) {
        changedFields.push({
          name: key, // or you can use a more descriptive name
          value: latestSummary[key], // current value
        });
      }
    }

    return changedFields;
  }

  const changedFields = getChangedFields(latestVersion, previousVersion);

  return (
    <>
      <Tabs
        tabs={tabs}
        selectedTabIndex={selectedTabIndex}
        setSelectedTabIndex={setSelectedTabIndex}
        showBorderBottom={false}
      />
      <div>
        {selectedTabIndex === 0 ? (
          <div className="divide-y-2">
            <FormFieldsCard
              fields={[
                {
                  key: "Registration Name",
                  label: "registrationName",
                  value: _get(
                    isHistory ? previousVersion.companySummary : company,
                    "registrationName",
                    "N/A"
                  ),
                },
                {
                  key: "Registration Number",
                  name: "registrationNumber",
                  value: _get(
                    isHistory ? previousVersion.companySummary : company,
                    "registrationNumber",
                    "N/A"
                  ),
                },
                {
                  key: "Entity Type",
                  name: "type",
                  value:
                    Constants.ENTITY_TYPE_AGAINST_TYPE[
                      _get(
                        isHistory ? previousVersion.companySummary : company,
                        "type",
                        "N/A"
                      )
                    ],
                },
              ]}
              hideResolveAllCheckbox
              changedFields={changedFields}
              isHistory={isHistory}
            />
            <FormFieldsCard
              wrapperStyles="mt-0"
              fields={[
                {
                  key: "Entity Status",
                  name: "status",
                  value: _get(
                    isHistory ? previousVersion.companySummary : company,
                    "status",
                    "N/A"
                  ),
                },
                {
                  key: "Registration Date",
                  name: "registrationDate",
                  value: company?.registrationDate
                    ? format(new Date(company?.registrationDate), "yyyy-MM-dd")
                    : "N/A",
                },
                {
                  key: "Nature of Business",
                  name: "natureOfBusiness",
                  value: _get(
                    isHistory
                      ? previousVersion.companySummary.articlesOfAssociation
                      : company,
                    "articlesOfAssociation.natureOfBusiness",
                    "N/A"
                  ),
                },
              ]}
              hideResolveAllCheckbox
              changedFields={changedFields}
              isHistory={isHistory}
            />
            <div className="px-4 space-y-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
              <div className="flex flex-col space-y-2">
                {[
                  {
                    key: "KRA PIN",
                    name: "kraPin",
                    value: _get(company, "kraPin", "N/A"),
                  },
                  {
                    key: "NSSF No.",
                    name: "NSSFNo",
                    value: _get(company, "NSSFNo", "N/A"),
                  },
                  {
                    key: "NHIF No.",
                    name: "NHIFNo",
                    value: _get(company, "NHIFNo", "N/A"),
                  },
                ].map((field, index) => {
                  const renderCompanyDocumentForm =
                    selectedField && selectedField.name === field.name;
                  return (
                    <React.Fragment key={`${field.key}_${index}`}>
                      <div className="flex flex-col">
                        <div className="flex items-baseline justify-between">
                          <label
                            htmlFor={field.name}
                            className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal"
                          >
                            {field.key}
                          </label>
                          <div
                            className={`bg-formInput flex ${
                              Constants.COOKIE_ROLES.CONTACT_PERSON ===
                              Cookies.get("role")
                                ? "w-[60%]"
                                : "w-full"
                            } justify-between relative text-sm text-primary`}
                          >
                            {field.value}
                          </div>
                          {dashboardRoles?.some(
                            (role) =>
                              role.name ===
                              Roles_Permissions_Constants.DASHBOARD_ROLES
                                .REGISTRAR
                          ) &&
                            !isHistory && (
                              <button
                                className="flex space-x-1 "
                                onClick={() => {
                                  handleFormOpen(field);
                                }}
                              >
                                <span>Edit</span>
                                <PencilIcon className="inline w-4 h-4" />
                              </button>
                            )}
                        </div>
                        <div
                          className={
                            renderCompanyDocumentForm ? "mt-5 mb-5" : ""
                          }
                        >
                          {renderCompanyDocumentForm && (
                            <RegistrarEditForm
                              field={selectedField}
                              handleFormClose={handleFormClose}
                            />
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
            <div className="px-4 space-y-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
              <div className="flex justify-between">
                {[
                  {
                    key: "Annual Return Date",
                    name: "registrationDate",
                    value: company?.annualReturnDate
                      ? `${format(
                          new Date(company?.annualReturnDate),
                          "dd/MM/yyyy"
                        ).slice(0, -2)}**`
                      : company?.registrationDate
                      ? `${format(
                          new Date(company?.registrationDate),
                          "dd/MM/yyyy"
                        ).slice(0, -2)}**`
                      : "N/A",
                  },
                  {
                    key: "Financial YE",
                    name: "accountingEndMonth",
                    value: company?.primaryBusinessActivity?.accountingEndMonth
                      ? format(
                          new Date(
                            new Date().getFullYear(),
                            company.primaryBusinessActivity.accountingEndMonth -
                              1,
                            1
                          ),
                          "MMMM"
                        )
                      : "N/A",
                  },
                ].map((field, index) => {
                  return (
                    <React.Fragment key={`${field.key}_${index}`}>
                      <div className="flex flex-col items-baseline justify-between">
                        <label
                          htmlFor={field.name}
                          className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal"
                        >
                          {field.key}
                        </label>
                        <p className="w-full p-2 text-sm whitespace-normal rounded-md bg-formInput text-primary font-medium placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300">
                          {field.value}
                        </p>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <>
            <h2 className="font-medium">Articles</h2>
            <TemplateSummary
              viewOnly
              selectedVersion={{
                summary: latestTemplateVersion?.summary,
                fileUploads: latestTemplateVersion?.fileUploads,
              }}
            />
            {contactPerson && (
              <FormFieldsCard
                wrapperStyles="mt-4"
                hideResolveAllCheckbox
                fields={[
                  {
                    name: "companyContactName",
                    key: "Full Name",
                    value: contactPerson.fullName || "N/A",
                  },
                  {
                    name: "companyContactDesignation",
                    key: "Designation",
                    value: contactPerson.designation || "N/A",
                  },
                  {
                    name: "telNumber",
                    key: "Telephone Number",
                    value: contactPerson.mobileNumber || "N/A",
                  },
                  {
                    name: "emailAddress",
                    key: "Email Address",
                    value: contactPerson.emailAddress || "N/A",
                  },
                ]}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
